import * as taxonomyUtils from "utils/taxonomy";

import {
  Account,
  CSSelectOption,
  DateFilterSearch,
  DateString,
  HtmlString,
  Question,
  QuestionAnswerChoiceSimple,
  QuestionCompareResultsFilterLabels,
  QuestionFilterLabelsBase,
  QuestionListFormValues,
  QuestionListTableFilterValues,
  QuestionResultsFilterLabels,
  QuestionSearchArgs,
  QuestionSearchResult,
  QuestionType,
  QuestionTypeBadgeBaseProps,
  QuestionTypeFilter,
  QuestionTypeFormat,
} from "types";
import { ErrorWithMessage, extractErrorMessageOrDefault } from "utils/errors";
import {
  QuestionInputType,
  QuestionOwnershipTerms,
  QuestionRoleType,
  QuestionSearchTextType,
  SharedQuestionTerms,
} from "consts";
import { arrayToDelimitedText, capitalizeWord } from "utils/base";
import { dateFilterSearchToDateRange, relativeDateToDateRange } from "utils/dates";
import { findInArrayByKeyValue, formatDate, isArray } from "@civicscience/chops";
import { getCheckboxGroupDetailUrl, getMatrixGroupDetailUrl } from "utils/questions/questionRouteUtils";

import { QuestionRoleTypeTerm } from "consts/QuestionRoleType";
import { SortTypes } from "utils/search/sorting";
import { querystring as qs } from "utils/url";
import { treeSearchUtils } from "utils";

export const findAnswerChoiceById = (
  answerChoices: QuestionAnswerChoiceSimple[] = [],
  id: string | undefined,
  fallback: string,
) => {
  const answerChoice = answerChoices && id ? findInArrayByKeyValue(answerChoices, "id", id) : undefined;

  return answerChoice?.text ?? fallback;
};

type getRolesFromQuestionRoleParamReturn = {
  isEngagement: true | null | undefined;
  isValue: true | null | undefined;
  isProfile: true | null | undefined;
};

export const getRolesFromQuestionRoleParam = (
  questionRole: string[] | null | undefined = [],
): getRolesFromQuestionRoleParamReturn => {
  const questionRoleIsArray = isArray(questionRole);

  return {
    isEngagement: (questionRoleIsArray && questionRole?.includes(QuestionRoleType.Engagement)) || null,
    isValue: (questionRoleIsArray && questionRole?.includes(QuestionRoleType.Value)) || null,
    isProfile: (questionRoleIsArray && questionRole?.includes(QuestionRoleType.Profile)) || null,
  };
};

/**
 * Gets the single string question role from the role object returned from search.
 */
export const getQuestionRoleFromRoles = (roles: QuestionSearchResult["roles"]): string => {
  if (roles.isEngagement) {
    return QuestionRoleType.Engagement;
  }

  if (roles.isProfile) {
    return QuestionRoleType.Profile;
  }

  if (roles.isValue) {
    return QuestionRoleType.Value;
  }

  return "";
};

type getOptionsFromSharedSharedCategoryParamReturn = {
  isTracking?: true | null;
  isTrending?: true | null;
  isCyclical?: true | null;
  isArchived?: true | null;
};

export const getOptionsFromSharedSharedCategoryParam = (
  questionSharedCategory: string[] | null | undefined = [],
): getOptionsFromSharedSharedCategoryParamReturn => {
  const sharedCategoryIsArray = isArray(questionSharedCategory);

  return {
    isTracking: (sharedCategoryIsArray && questionSharedCategory?.includes(SharedQuestionTerms.TRACKING)) || null,
    isTrending: (sharedCategoryIsArray && questionSharedCategory?.includes(SharedQuestionTerms.TRENDING)) || null,
    isCyclical: (sharedCategoryIsArray && questionSharedCategory?.includes(SharedQuestionTerms.CYCLICAL)) || null,
    isArchived: (sharedCategoryIsArray && questionSharedCategory?.includes(SharedQuestionTerms.ARCHIVED)) || null,
  };
};

export const defaultTypeFilterClearValues = {
  questionType: [],
  questionSharedCategory: [],
  inputType: [],
  isMyFavoritesOnly: false,
  questionRole: [],
};

export const emptyQuestionSearchTypeFilterValues = {
  questionType: [],
  questionSharedCategory: [],
  inputType: [],
  isMyFavoritesOnly: false,
  questionRole: [],
};

export const emptyQuestionSearchFilterValues: QuestionListFormValues = {
  query: "",
  tags: [],
  taxonomyTags: [],
  categories: [],
  dateFilter: { kind: "all" },
  responseCountGte: 0,
  searchText: [],
  ...emptyQuestionSearchTypeFilterValues,
};

export const defaultQuestionSearchTypeFilterValues = {
  questionType: [QuestionOwnershipTerms.CUSTOM, QuestionOwnershipTerms.SYNDICATED],
  questionSharedCategory: [],
  isMyFavoritesOnly: false,
  inputType: [QuestionInputType.Radio, QuestionInputType.Checkbox, QuestionInputType.Matrix],
  responseCountGte: 0,
};

export const defaultQuestionSearchFilterValues: QuestionListFormValues = {
  query: "",
  tags: [],
  taxonomyTags: [],
  categories: [],
  dateFilter: { kind: "all" },
  searchText: [QuestionSearchTextType.QUESTIONS],
  questionRole: [QuestionRoleType.Value, QuestionRoleType.Profile],
  ...defaultQuestionSearchTypeFilterValues,
};

export const defaultDashletQuestionSearchFilterValues: QuestionListFormValues = {
  query: "",
  tags: [],
  taxonomyTags: [],
  categories: [],
  dateFilter: { kind: "all" },
  searchText: [QuestionSearchTextType.QUESTIONS],
  questionRole: [QuestionRoleType.Value, QuestionRoleType.Profile, QuestionRoleType.Engagement],
  ...defaultQuestionSearchTypeFilterValues,
};

export const getDefaultQuestionTypes = (isInternal: boolean) => {
  return [
    QuestionOwnershipTerms.CUSTOM,
    QuestionOwnershipTerms.SYNDICATED,
    ...(isInternal ? [QuestionOwnershipTerms.CIVICSCIENCE] : []),
  ];
};

/**
 * Returns the query params for default search
 */
export const buildDefaultQuestionSearchFilterValues = (isInternal: boolean) => {
  return {
    ...defaultQuestionSearchFilterValues,
    questionType: getDefaultQuestionTypes(isInternal),
  };
};

export const defaultQuestionSearchTableFilterValues = {
  sortTerm: SortTypes.RELEVANCE,
  page: 1,
  size: 25,
};

type QuestionSearchDateFilterParams = {
  startDate: Date | null;
  endDate: Date | null;
  isCurrentlyLive: boolean;
};

type QuestionListParamOverridesProps = {
  // consider naming questionType something else as to not confuse with 'radio' | 'checkbox'
  questionType?: QuestionTypeFilter | QuestionTypeFilter[];
  inputType?: string[];
  sortTerm?: string;
  isMyFavoritesOnly?: boolean;
  taxonomyTags?: string | string[];
  questionRole?: string[];
  responseCountGte?: number;
  searchText?: string[];
};

const buildWithEmptyQuestionParams = (paramOverrides: QuestionListParamOverridesProps) => {
  const emptyFormFilterValues: QuestionListFormValues = { ...emptyQuestionSearchFilterValues };
  return qs.buildQueryParams({ ...emptyFormFilterValues, ...paramOverrides });
};

const buildWithDefaultQuestionParams = (paramOverrides: QuestionListParamOverridesProps) => {
  const defaultFormFilterValues: QuestionListFormValues = { ...defaultQuestionSearchFilterValues };
  return qs.buildQueryParams({ ...defaultFormFilterValues, ...paramOverrides });
};

/**
 * Returns the query params for a "Favorite Questions" search
 */
export const getFavoriteQuestionsSearchParams = (isInternal: boolean): string =>
  buildWithEmptyQuestionParams({
    isMyFavoritesOnly: true,
    inputType: [QuestionInputType.Radio, QuestionInputType.Checkbox],
    questionRole: [QuestionRoleType.Engagement, QuestionRoleType.Value, QuestionRoleType.Profile],
    questionType: getDefaultQuestionTypes(isInternal),
    searchText: [QuestionSearchTextType.QUESTIONS],
  });

/**
 * Returns the query params for a "Custom Questions" search
 */
export const getCustomQuestionsSearchParams = (): string =>
  buildWithDefaultQuestionParams({
    questionType: [QuestionOwnershipTerms.CUSTOM],
    inputType: [QuestionInputType.Radio, QuestionInputType.Checkbox],
    questionRole: [QuestionRoleType.Engagement, QuestionRoleType.Value, QuestionRoleType.Profile],
    searchText: [QuestionSearchTextType.QUESTIONS],
  });

/**
 * Returns the query params for a "New Questions" search
 */
export const getNewQuestionsSearchParams = (isInternal: boolean): string =>
  buildWithDefaultQuestionParams({
    inputType: [QuestionInputType.Radio, QuestionInputType.Checkbox],
    sortTerm: SortTypes.NEWEST_CREATED,
    questionRole: [QuestionRoleType.Value, QuestionRoleType.Profile],
    questionType: getDefaultQuestionTypes(isInternal),
    responseCountGte: 1000,
    searchText: [QuestionSearchTextType.QUESTIONS],
  });

/**
 * Returns the query params for a "Trending Questions" search
 */
export const getTrendingQuestionsSearchParams = (isInternal: boolean): string => {
  const trendingTag = taxonomyUtils.encodeTrendingTagValue();
  return buildWithDefaultQuestionParams({
    inputType: [QuestionInputType.Radio, QuestionInputType.Checkbox],
    taxonomyTags: trendingTag,
    questionType: getDefaultQuestionTypes(isInternal),
    questionRole: [QuestionRoleType.Value, QuestionRoleType.Profile],
    searchText: [QuestionSearchTextType.QUESTIONS],
  });
};

/**
 * Returns the array for Favorites button by looking at current isMyFavoritesOnly value
 */
export const getFavoritesDefault = (isMyFavoritesOnly = false) => {
  return isMyFavoritesOnly ? [SharedQuestionTerms.FAVORITES] : [];
};

/**
 * Maps `DateFilterSearch` to the params that are passed to the search end point.
 * This returns an object so that you can spread/merge it with your existing params.
 */
export const getQuestionSearchDateFilterParams = (
  dateFilterSearch: DateFilterSearch,
): QuestionSearchDateFilterParams => {
  const empty: QuestionSearchDateFilterParams = { isCurrentlyLive: false, startDate: null, endDate: null };

  switch (dateFilterSearch.kind) {
    case "all":
    case "clear-current-value":
      return empty;
    case "live":
      return { ...empty, isCurrentlyLive: true };
    case "custom-fixed":
      return { ...empty, startDate: dateFilterSearch.startDate, endDate: dateFilterSearch.endDate };
    case "fixed-relative":
      return { ...empty, ...dateFilterSearchToDateRange(dateFilterSearch) };
    case "custom-relative":
      return { ...empty, ...relativeDateToDateRange(dateFilterSearch) };
  }
};

type GetQuestionSearchParamsProps = QuestionListFormValues;

/**
 * Maps the values from the question search form to the parameters required by the question search API.
 */
export const getQuestionSearchParams = ({
  dateFilter,
  taxonomyTags,
  categories,
  ...formValues
}: GetQuestionSearchParamsProps): QuestionSearchArgs => {
  const hasQuestionRole = formValues.questionRole && formValues.questionRole.length > 0;
  const questionOrClassificationRoleValues = hasQuestionRole
    ? getRolesFromQuestionRoleParam(formValues.questionRole)
    : [];

  return {
    ...formValues,
    ...questionOrClassificationRoleValues,
    ...getOptionsFromSharedSharedCategoryParam(formValues.questionSharedCategory),
    ...getQuestionSearchDateFilterParams(dateFilter),
    ...taxonomyUtils.buildApiValues(taxonomyTags),
    categories: treeSearchUtils.decodeNodeIds(categories),
    searchAnswerOptions: formValues.searchText.includes(QuestionSearchTextType.ANSWERS),
    searchQuestionText: formValues.searchText.includes(QuestionSearchTextType.QUESTIONS),
    searchTags: formValues?.searchText.includes(QuestionSearchTextType.TAGS),
    searchTranslations: formValues?.searchText.includes(QuestionSearchTextType.TRANSLATIONS),
    responseCountGte: formValues.responseCountGte || null,
  };
};

const formatLabelDate = (date: string) =>
  formatDate(date, {
    month: "short",
  });

const getDateFilterLabel = (
  startDate: DateString | null | undefined,
  endDate: DateString | null | undefined,
): HtmlString => {
  if (startDate && endDate) {
    return `from ${formatLabelDate(startDate)} to ${formatLabelDate(endDate)}`;
  }

  if (startDate) {
    return `on or after ${formatLabelDate(startDate)}`;
  }

  if (endDate) {
    return `on or before ${formatLabelDate(endDate)}`;
  }

  return "";
};

type GetAppliedFiltersHtmlProps = QuestionFilterLabelsBase & {
  suffixText?: string;
  hideNetworkText?: boolean;
};

/**
 * Returns a string (may include HTML) representing the provided filters.
 *
 * Any combination of filters may be enabled and the resulting label will still read appropriately.
 */
export const getAppliedFiltersHtml = ({
  startDate,
  endDate,
  segmentName,
  targetName,
  networkName,
  weightingSchemeLabel,
  suffixText,
  hideNetworkText = false,
}: GetAppliedFiltersHtmlProps): HtmlString => {
  return [
    targetName && `for target ${targetName}`,
    segmentName && `in segment ${segmentName}`,
    getDateFilterLabel(startDate, endDate),
    !hideNetworkText && (networkName ? `in network ${networkName}` : "in my account"),
    weightingSchemeLabel && `weighted according to ${weightingSchemeLabel}`,
    suffixText && `| ${suffixText}`,
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Returns a string (may include HTML) representing the provided filters.
 *
 * Any combination of filters may be enabled and the resulting label will still read appropriately.
 */
export const getAppliedFiltersLabel = ({
  startDate,
  endDate,
  segmentName,
  targetName,
  networkName,
  weightingSchemeLabel,
  valueGroupingLabel,
  hideNetworkText = false,
}: QuestionResultsFilterLabels): HtmlString => {
  return getAppliedFiltersHtml({
    startDate,
    endDate,
    segmentName,
    targetName,
    networkName,
    weightingSchemeLabel,
    suffixText: valueGroupingLabel ? `Answers grouped by ${valueGroupingLabel}` : undefined,
    hideNetworkText,
  });
};

type GetAppliedFiltersObjectProps = {
  filters: QuestionResultsFilterLabels;
  account?: Account;
};

/**
 * Returns a string (may include HTML) representing the provided filters.
 *
 * Any combination of filters may be enabled and the resulting label will still read appropriately.
 */
export const getAppliedFiltersLabelWithAccount = ({ filters, account }: GetAppliedFiltersObjectProps): HtmlString => {
  return getAppliedFiltersLabel(Object.assign({}, filters ?? {}, { hideNetworkText: account?.isClient ?? false }));
};

/**
 * Returns a string (may include HTML) representing the provided filters.
 *
 * Any combination of filters for compare charts may be enabled and the resulting label will still read appropriately.
 */
export const getAppliedCompareFiltersLabel = ({
  startDate,
  endDate,
  segmentName,
  targetName,
  networkName,
  weightingSchemeLabel,
  hideNetworkText,
}: QuestionCompareResultsFilterLabels): HtmlString => {
  return getAppliedFiltersHtml({
    startDate,
    endDate,
    segmentName,
    targetName,
    networkName,
    weightingSchemeLabel,
    hideNetworkText,
  });
};

export const strengthOfAssociationText = (tValue: number) => {
  let tValText = "";
  if (tValue < 0.05) {
    tValText = "Low";
  } else if (tValue < 0.1) {
    tValText = "Medium";
  } else if (tValue < 0.2) {
    tValText = "High";
  } else {
    tValText = "Very High";
  }

  return `${tValText} Strength of Association`;
};

type GetQuestionResultsErrorMsgProps = {
  error?: ErrorWithMessage;
  fallback?: string;
};

export const getQuestionResultsErrorMsg = ({
  error,
  fallback = "Error loading question results data",
}: GetQuestionResultsErrorMsgProps) => extractErrorMessageOrDefault(error, fallback);

/**
 * Builds question search params into a human-readable string.
 * NOTE: This is biased towards question list dashlets at the moment.
 * If we want to support the full question search we will most likely need to add to this function.
 */
export const getQuestionSearchFilterLabel = (filters: QuestionListFormValues & QuestionListTableFilterValues) => {
  const { startDate, endDate } = dateFilterSearchToDateRange(filters.dateFilter);

  const filterTextParts = [
    filters.isMyFavoritesOnly ? "my favorite" : null,
    filters.questionType && filters.questionType.length === 1
      ? getQuestionOwnershipLabel(filters.questionType[0])
      : null,
    filters.questionRole ? arrayToDelimitedText(filters.questionRole.map(getQuestionRoleLabel)) : null,
    "questions",
    getDateFilterLabel(startDate?.toString(), endDate?.toString()),
    // TODO: Do we want to add in sort information here?
    // "ordered from newest to oldest", "ordered by responses", etc.?
  ].filter(Boolean);

  // if the only entry is the static "questions" entry then return empty as there are no applied filters.
  if (filterTextParts.length === 1) {
    return "";
  }

  return capitalizeWord(filterTextParts.join(" "));
};

/**
 * Converts a `QuestionRoleType` to a display string.
 * If the `role` can't be matched an empty string is returned.
 */
export const getQuestionRoleLabel = (role: string): string => {
  switch (role.toLowerCase()) {
    case QuestionRoleType.Engagement:
      return "Engagement";
    case QuestionRoleType.Profile:
      return "Profile";
    case QuestionRoleType.Value:
      return "Value";
    case QuestionRoleType.Quiz:
      return "Quiz";
  }

  return "";
};

export const getQuestionOwnershipLabel = (ownership: string): string => {
  switch (ownership.toLowerCase()) {
    case QuestionOwnershipTerms.CUSTOM:
      return "Custom";
    case QuestionOwnershipTerms.SYNDICATED:
      return "Syndicated";
    case QuestionOwnershipTerms.CIVICSCIENCE:
      return "CivicScience";
  }
  return "";
};

export const searchTextGroupOptions: CSSelectOption[] = [
  { text: "Questions", value: QuestionSearchTextType.QUESTIONS },
  { text: "Answers", value: QuestionSearchTextType.ANSWERS },
  { text: "Tags", value: QuestionSearchTextType.TAGS },
  { text: "Translations", value: QuestionSearchTextType.TRANSLATIONS },
];

export const questionRoleGroupOptions: CSSelectOption[] = [
  { text: getQuestionRoleLabel(QuestionRoleType.Engagement), value: QuestionRoleType.Engagement },
  { text: getQuestionRoleLabel(QuestionRoleType.Value), value: QuestionRoleType.Value },
  { text: getQuestionRoleLabel(QuestionRoleType.Profile), value: QuestionRoleType.Profile },
];

export const ownershipGroupOptions: CSSelectOption[] = [
  { text: "Custom", value: QuestionOwnershipTerms.CUSTOM },
  { text: "Syndicated", value: QuestionOwnershipTerms.SYNDICATED },
];

export const ownershipGroupOptionsInternal: CSSelectOption[] = [
  ...ownershipGroupOptions,
  { text: "CivicScience", value: QuestionOwnershipTerms.CIVICSCIENCE },
];

export const favoritesGroupOptions: CSSelectOption[] = [{ text: "Favorites", value: SharedQuestionTerms.FAVORITES }];

export const questionSharedCategoryGroupOptions: CSSelectOption[] = [
  { text: "Tracking", value: SharedQuestionTerms.TRACKING },
  { text: "Cyclical", value: SharedQuestionTerms.CYCLICAL },
  { text: "Archived", value: SharedQuestionTerms.ARCHIVED },
];

export const inputTypeGroupOptions: CSSelectOption[] = [
  { text: "Radio", value: QuestionInputType.Radio },
  { text: "Checkbox", value: QuestionInputType.Checkbox },
  { text: "Matrix", value: QuestionInputType.Matrix },
];

export const QuestionRepeatTypeInt = {
  Never: 2147483647,
  Immediately: 0,
  After: 1,
};

/**
 * Get text to display how often a question can be asked based
 * on repeatIneligibleSeconds.
 */
export const repeatableStatusText = (repeatIneligibleSeconds: number) => {
  if (repeatIneligibleSeconds === QuestionRepeatTypeInt.Never) {
    return "Never";
  } else if (repeatIneligibleSeconds === QuestionRepeatTypeInt.Immediately) {
    return "Immediately";
  } else {
    return `After ${repeatIneligibleSeconds / 86400} days`;
  }
};

/**
 * A general interface for a question with a `qtype` OR `type` and optional `checkboxGroup` or `askableParent`.
 * Allows `getQuestionTypeFormat` to work with both open search and question detail responses.
 */
type QuestionWithAskableParent = {
  // named this way in open search
  type?: QuestionType;
  // named this way in question detail API responses
  qtype?: QuestionType;
  askableParent?: { type?: "checkbox" | "matrix" | null; id?: number | null } | null;
};

/**
 * Determines the `QuestionTypeFormat` based on the provided `QuestionWithType`.
 * This will **always** return a value - with the default being "radio".
 */
export const getQuestionTypeFormat = (question: QuestionWithAskableParent): QuestionTypeFormat => {
  // to determine between "checkbox group" and "radio" we check `qtype` and `type`.
  // we'll default to "radio" in the (not expected) case that both are falsy.
  const qtype: QuestionType = question.qtype || question.type || "radio";
  return qtype === "checkbox" ? "checkbox-group" : qtype === "matrix" ? "matrix-group" : "radio";
};

/**
 * Builds the props for `QuestionTypeBadge` based on the provided `item`.
 *
 * Checkbox items will be hyperlinked to the checkbox group detail page.
 */
export const getQuestionTypeBadgeProps = (item: QuestionWithAskableParent): QuestionTypeBadgeBaseProps => {
  if (item.type === "checkbox") {
    return { questionTypeFormat: "checkbox-group" };
  }

  if (item.type === "matrix") {
    return { questionTypeFormat: "matrix-group" };
  }

  const askableParentId = item.askableParent?.id?.toString() ?? "";
  switch (item.askableParent?.type) {
    case "checkbox":
      return {
        questionTypeFormat: "checkbox-item",
        title: `Checkbox Group: ${askableParentId}`,
        href: getCheckboxGroupDetailUrl({ questionId: askableParentId }),
      };
    case "matrix":
      return {
        questionTypeFormat: "matrix-item",
        title: `Matrix Group: ${askableParentId}`,
        href: getMatrixGroupDetailUrl({ questionId: askableParentId }),
      };
    default:
      return { questionTypeFormat: "radio" };
  }
};

type HasQuestionRole = Pick<Question, "hasRoleEngagement" | "hasRoleValue" | "hasRoleProfile">;

/**
 * Returns the `QuestionRoleTypeTerm` based on the provided `question`.
 * @param question - The question object which may have role properties.
 * @param includeQuiz - Whether to consider the question as quiz. Defaults to false.
 *
 * Param includeQuiz should be passed when the given question has a quizAnswer property,
 * and the type is relevant. Optional because quiz questions are a subtype of engagement.
 */
export const getQuestionRoleType = (question: HasQuestionRole, includeQuiz: boolean = false): QuestionRoleTypeTerm => {
  if (question.hasRoleEngagement) {
    if (includeQuiz) {
      return QuestionRoleType.Quiz;
    }
    return QuestionRoleType.Engagement;
  }

  if (question.hasRoleValue) {
    return QuestionRoleType.Value;
  }

  if (question.hasRoleProfile) {
    return QuestionRoleType.Profile;
  }

  return QuestionRoleType.Engagement;
};
